import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { dealApi } from '../services/pipelineApi';
import type { DealBulkScope } from '../services/pipelineApi';
import type { DealCreate, DealUpdate, ListDealsParams, DealListFilterParams } from '../types';
import { pipelineKeys } from './usePipelines';
import { invalidateBacklinks } from '../../../shared/components/backlinks/useBacklinkPanels';

/** Canonical `module.entity` key of the subject these deals back-link to. */
const CONTACT_SUBJECT_TYPE = 'master_data.contact';
import { useLastUsedPipeline } from './useLastUsedPipeline';

export const dealKeys = {
  all: ['deals'] as const,
  list: (params?: ListDealsParams) => [...dealKeys.all, 'list', params] as const,
  aggregates: (params?: DealListFilterParams) => [...dealKeys.all, 'aggregates', params] as const,
  detail: (id: string) => [...dealKeys.all, 'detail', id] as const,
};

/**
 * Clean undefined values from params for consistent query key comparison.
 * React Query does deep comparison, but { key: undefined } vs {} can cause issues.
 */
function cleanParams(params?: ListDealsParams): ListDealsParams | undefined {
  if (!params) return undefined;
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined)
  ) as ListDealsParams;
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

export function useDeals(params?: ListDealsParams) {
  // Memoize cleaned params to prevent unnecessary re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableParams = useMemo(() => cleanParams(params), [
    params,
    params?.search,
    params?.pipeline_id,
    params?.stage_id,
    params?.organization_id,
    params?.owner_id,
    params?.partner_id,
    params?.team_filter,
    params?.stage_type,
    params?.archived_only,
    params?.include_archived,
    params?.custom_fields,
    params?.admin_scope,
    params?.limit,
    params?.offset,
    params?.sort_by,
    params?.sort_direction,
  ]);

  return useQuery({
    queryKey: dealKeys.list(stableParams),
    queryFn: () => dealApi.list(stableParams),
    // ui-list-pages.md §2: search is debounced into the query key, so without
    // this the hook falls back to `isPending` on every keystroke and the table
    // blanks between results.
    placeholderData: keepPreviousData,
  });
}

/**
 * Aggregate deal-value figures over the whole filtered set (ui-list-pages.md §11).
 *
 * Keyed on the filters ALONE, which is the point of the separate request: paging
 * through the list doesn't refetch or invalidate it, because turning a page does
 * not change what the figures describe.
 */
export function useDealAggregates(params?: DealListFilterParams) {
  return useQuery({
    queryKey: dealKeys.aggregates(params),
    queryFn: () => dealApi.aggregates(params),
    placeholderData: keepPreviousData,
  });
}

export function useDeal(id: string) {
  return useQuery({
    queryKey: dealKeys.detail(id),
    queryFn: () => dealApi.get(id),
    enabled: !!id,
  });
}

export function useCreateDeal() {
  const queryClient = useQueryClient();
  const { rememberPipeline } = useLastUsedPipeline();
  return useMutation({
    mutationFn: (data: DealCreate) => dealApi.create(data),
    onSuccess: (deal) => {
      // Remember the chosen pipeline so the next New Deal pre-selects it.
      rememberPipeline(deal.pipeline_id);
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.summary(deal.pipeline_id) });
    },
  });
}

export function useUpdateDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DealUpdate }) => dealApi.update(id, data),
    onSuccess: (deal, { id }) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.summary(deal.pipeline_id) });
      if (deal.primary_contact_id) {
        void invalidateBacklinks(queryClient, CONTACT_SUBJECT_TYPE, deal.primary_contact_id);
      }
    },
  });
}

export function useDeleteDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dealApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}

export function useMoveDealToStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dealId, stageId, extra }: { dealId: string; stageId: string; fromPipelineId?: string; extra?: { loss_reason?: string; competitor?: string; actual_close_date?: string } }) =>
      dealApi.moveToStage(dealId, stageId, extra),
    onSuccess: (deal, { fromPipelineId }) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.detail(deal.id) });
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.summary(deal.pipeline_id) });
      // Cross-pipeline move: the source pipeline's summary/totals also changed.
      if (fromPipelineId && fromPipelineId !== deal.pipeline_id) {
        queryClient.invalidateQueries({ queryKey: pipelineKeys.summary(fromPipelineId) });
      }
    },
  });
}

export function useAddContactToDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dealId,
      contactId,
      role,
      isPrimary,
    }: {
      dealId: string;
      contactId: string;
      role?: string;
      isPrimary?: boolean;
    }) => dealApi.addContact(dealId, contactId, role, isPrimary),
    onSuccess: (_, { dealId, contactId }) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
      void invalidateBacklinks(queryClient, CONTACT_SUBJECT_TYPE, contactId);
    },
  });
}

export function useUpdateDealContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dealId,
      contactId,
      role,
      isPrimary,
    }: {
      dealId: string;
      contactId: string;
      role?: string;
      isPrimary?: boolean;
    }) => dealApi.updateContact(dealId, contactId, { role, is_primary: isPrimary }),
    onSuccess: (_, { dealId, contactId }) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
      void invalidateBacklinks(queryClient, CONTACT_SUBJECT_TYPE, contactId);
    },
  });
}

export function useRemoveContactFromDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dealId, contactId }: { dealId: string; contactId: string }) =>
      dealApi.removeContact(dealId, contactId),
    onSuccess: (_, { dealId, contactId }) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
      void invalidateBacklinks(queryClient, CONTACT_SUBJECT_TYPE, contactId);
    },
  });
}

export function useDuplicateDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dealApi.duplicate(id),
    onSuccess: (deal) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.summary(deal.pipeline_id) });
    },
  });
}

export function useArchiveDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dealApi.archive(id),
    onSuccess: (deal) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.detail(deal.id) });
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.summary(deal.pipeline_id) });
    },
  });
}

export function useUnarchiveDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dealApi.unarchive(id),
    onSuccess: (deal) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.detail(deal.id) });
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.summary(deal.pipeline_id) });
    },
  });
}

export function useBulkDeleteDeals() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scope: DealBulkScope) => dealApi.bulkDelete(scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}

export function useBulkRestoreDeals() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => dealApi.bulkRestore(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}

export function useBulkArchiveDeals() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scope: DealBulkScope) => dealApi.bulkArchive(scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}

/**
 * Reassign deals to one owner — or clear it — the deal list's escalatable bulk
 * action.
 *
 * Takes the same discriminated scope the API does, so a caller cannot send an id
 * list and `match_all_filtered` together, or neither. `ownerId` of `null` is the
 * clear, not "no argument": see `pipelineApi.bulkAssignOwner`.
 */
export function useBulkAssignDealOwner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      scope,
      ownerId,
    }: {
      scope: { ids: string[] } | { matchAllFiltered: true; filters: DealListFilterParams };
      ownerId: string | null;
    }) => dealApi.bulkAssignOwner(scope, ownerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}
